import { Stripe, StripeElement, StripeElements, StripePaymentElement } from "@stripe/stripe-js";
import { FuelType, MeterType } from "./meter.model";

export interface CheckboxConsent {
  viewedTimestamp: string;
  checkedTimestamp: string;
  method: string;
  accepted: boolean;
}

export type StripeConsent = {
  stripe: Stripe | null;
  stripeElements: StripeElements | null;
  stripePaymentElement: StripePaymentElement;
  stripeCredentials: StripeCredentials;
};

export interface Consent {
  consentCategory: ConsentCategory; // Category of consent (e.g., LOA, CHECKBOX, SMART_METER)
  consentType: ConsentType; // Type of consent (e.g., LOA, checkbox, smart)
  // Consent data payload varies by consentType:
  // MAC_ADDRESS -> string
  // BILL_UPLOAD -> File
  // STRIPE -> { stripePaymentElement: StripePaymentElement }
  // CHECKBOX -> CheckboxConsent
  consentData: File | string | CheckboxConsent | { stripePaymentElement: any }; // Stripe element typed as any to avoid importing stripe types here
  scope: 'HH' | 'ANNUAL'; // Granularity of the data
  moveInDate?: Date; // Date when the consent was given
  termsAccepted?: boolean; // Indicates if the terms were accepted (Only for smart meters)
} 

export type SmartConsentValue =
  | { consentType: ConsentType.MAC_ADDRESS; consentData: string; consentCategory: ConsentCategory.SMART_METER }
  | { consentType: ConsentType.BILL_UPLOAD; consentData: File; consentCategory: ConsentCategory.SMART_METER }
  | { consentType: ConsentType.STRIPE; consentData: StripeConsent; consentCategory: ConsentCategory.SMART_METER }
  | { consentType: ConsentType.CHECKBOX; consentData: CheckboxConsent; consentCategory: ConsentCategory.CHECKBOX };


export interface SharedConsent {
  shareId?: string;
  consentId?: string;
  consentRequestId: string;
  mpans: string[];
  address: string;
  meterType: string;
  fuelType?: string; // Adding fuelType for better consistency
  consentStatus: string;
  partnerEmail?: string;
  partnerOrg: string;
  softwarePlatform?: string;
  shareType: 'platform' | 'link' | 'email'; // Type of sharing
  emailSentTo?: string;
  consentStartDate?: Date;
  consentEndDate?: Date;
  meterNo?: string;
  dataLink?: string; // Link to download data if applicable
  createdAt?: Date;
}

export interface SharedConsentApiResponse {
  status: boolean;
  code: string;
  timestamp: string;
  message: string;
  data: {
    customerEmail: string;
    totalSharedConsents: number;
    sharedConsents: Array<{
      shareId: string;
      consentRequestId: string;
      consentId: string;
      agentEmail: string;
      agentOrg: string;
      agentOrgId: string;
      customerEmail: string;
      customerOrgName: string;
      customerOrgNumber: string;
      softwareName: string;
      customerRole: string;
      siteAddress: string;
      meterNumber: string;
      meterType: string;
      fuelType: string;
      scope: string;
      shareType: 'platform' | 'link' | 'email'; // Type of sharing
      consentStartDate: string;
      consentStopDate: string;
      shareStartDate: string;
      shareStopDate: string;
      originalConsentStartDate: string;
      originalConsentEndDate: string;
      mpxns: string[];
      status: string;
      createdAt: string;
      updatedAt: string;
      emailSentTo?: [
        {
          email: string;
          status: string;
          sharedDateTime: string;
        }
      ]
    }>;
  };
  errors: any[];
  additionalInfo: Record<string, any>;
}

export interface ApprovedMetersApiResponse {
  status: boolean;
  code: string;
  timestamp: string;
  message: {
    email: string;
    approvedMeters: Array<{
      meter: string;
      meterType: string;
      fuelType: string;
      consentStartDate: string;
      consentEndDate: string;
      consentId: string;
    }>;
  };
  errors: any[];
  additionalInfo: Record<string, any>;
}

export interface DigitalSignature {
  email: string;
  companyName: string;
  fullName: string;
  role: string;
  sinceDate: string;
  meters: Array<{
    mpan: string;
    address: string;
  }>;
  relationship: string;
}


export interface MeterDetailsFromConsentRequest {
  mpxn: string;
  fuelType: FuelType;
  address?: string;
  profileClass?: string;
  type?: string;   
  supplierName?: string;
  uprn?: string | null;
}

export interface ConsentRequest {
  consentRequestId: string;
  meters: Array<MeterDetailsFromConsentRequest>;
  agentOrgId?: string;
  agentOrg?: string;
  agentEmail?: string;
  customerEmail: string;
  customConfig?: any
  // Add other fields as needed based on the API response
}

export interface ConsentRequestSummary {
  consentRequestId: string;
  agentOrg: string;
  meterCount: number;
  requestedDate: string;
  status: string;
  notificationStatus: NotificationStatus;
  mode: string;
}

export interface ConsentRequestApiResponse {
  status: boolean;
  code: string;
  timestamp: string;
  message: ConsentRequest;
  errors: any[];
  additionalInfo: Record<string, any>;
}

export enum RelationshipType {
  OCCUPIER = 'occupier',
  BILL_PAYER = 'bill_payer',
  LANDLORD = 'landlord',
}

export enum ConsentType {
  LOA_UPLOAD = 'LOA_UPLOAD',
  DIGITAL_SIGNATURE = 'DIGITAL_SIGNATURE',
  CHECKBOX = 'CHECKBOX',
  MAC_ADDRESS = 'MAC_ADDRESS',
  BILL_UPLOAD = 'BILL_UPLOAD',
  STRIPE = 'STRIPE'
}

export enum ConsentCategory {
  LOA = 'LOA',
  CHECKBOX = 'CHECKBOX',
  SMART_METER = 'SMART_METER'
}

export enum NotificationStatus {
  NEW = 'NEW',
  CLEARED = 'CLEARED',
  DISMISSED = 'DISMISSED',
  VIEWED = 'VIEWED'
}

export interface RequestConsentAPIPayload {
  sendEmail?: boolean; // Optional field to control email sending
  agentOrg: string;
  agentOrgId?: string;
  customerEmail: string;
  portfolioId?: string;
  meters?: Array<{
    mpxn: string;
    fuelType: string;
  }>;
}

export interface StripeConsentPayload {
  consentType: 'STRIPE';
  meterNo: string;
  moveInDate: string; // Format: "YYYY-MM-DD"
  email: string;
  postCode: string;
  city: string;
  country: string;
  addressLine: string;
  termAndConditionAccepted: 'yes' | 'no';
}

export interface StripeCredentials {
  stripeCustomerId: string;
  stripeSetupIntentId: string;
  stripeSecret: string;
  publishable_key: string;
}

export interface StripeConsentApiResponse {
  status: boolean;
  code: number;
  timestamp: string;
  message: {
    stripeCustomerId: string;
    stripeSetupIntentId: string;
    stripeSecret: string;
    publishable_key: string;
    // Additional fields that might be present but we don't need for the credentials
    [key: string]: any;
  };
  data: any[];
  errors: any[];
  additionalInfo: Record<string, any>;
}